/**
 * Product Customizer & Illustrated Wizard - Shared Utilities Module
 * @version 1.0.0
 * @author Senior JavaScript Expert
 * 
 * Provides shared utilities, PCWTooltip component, standardized image uploader,
 * spinner controller, SHA-256 crypto hashing, and local cache helpers.
 */

(function (window, document) {
    'use strict';

    /**
     * Custom Tooltip Component
     * Auto-applies to all elements with class 'tooltip' and 'data-title' attribute
     */
    const PCWTooltip = (function () {
        let tooltipEl = null;

        function createTooltip() {
            if (tooltipEl) return tooltipEl;
            tooltipEl = document.createElement('div');
            tooltipEl.className = 'pcw-custom-tooltip';
            document.body.appendChild(tooltipEl);
            return tooltipEl;
        }

        function showTooltip(e) {
            const target = e.currentTarget;
            const title = target.getAttribute('data-title');
            if (!title) return;

            const tooltip = createTooltip();
            tooltip.textContent = title;
            tooltip.classList.add('visible');

            updatePosition(e);
            target.addEventListener('mousemove', updatePosition);
        }

        function hideTooltip(e) {
            if (!tooltipEl) return;
            tooltipEl.classList.remove('visible');
            e.currentTarget.removeEventListener('mousemove', updatePosition);
        }

        function updatePosition(e) {
            if (!tooltipEl) return;
            const x = e.clientX;
            const y = e.clientY;
            const offset = 15;

            let top = y - tooltipEl.offsetHeight - offset;
            let left = x - (tooltipEl.offsetWidth / 2);

            if (top < 0) {
                top = y + offset;
                tooltipEl.style.setProperty('--arrow-pos', 'top');
            }
            if (left < 0) left = 5;
            if (left + tooltipEl.offsetWidth > window.innerWidth) {
                left = window.innerWidth - tooltipEl.offsetWidth - 5;
            }

            tooltipEl.style.top = top + 'px';
            tooltipEl.style.left = left + 'px';
        }

        return {
            init: function () {
                const elements = document.querySelectorAll('.tooltip[data-title]');
                elements.forEach(el => {
                    el.addEventListener('mouseenter', showTooltip);
                    el.addEventListener('mouseleave', hideTooltip);
                });
            }
        };
    })();

    // Auto-init PCWTooltip on DOM ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => PCWTooltip.init());
    } else {
        PCWTooltip.init();
    }

    /**
     * SHA-256 Hashing Implementation (Web Crypto API with pure JS fallback)
     */
    async function sha256(text) {
        if (!text) return '';
        if (window.crypto && window.crypto.subtle) {
            try {
                const encoder = new TextEncoder();
                const data = encoder.encode(text);
                const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
                const hashArray = Array.from(new Uint8Array(hashBuffer));
                return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
            } catch (e) {
                console.warn('SubtleCrypto failed, using fallback SHA256:', e);
            }
        }
        return sha256_fallback(text);
    }

    function sha256_fallback(str) {
        function rotateRight(n, x) { return (x >>> n) | (x << (32 - n)); }
        function sig0(x) { return rotateRight(7, x) ^ rotateRight(18, x) ^ (x >>> 3); }
        function sig1(x) { return rotateRight(17, x) ^ rotateRight(19, x) ^ (x >>> 10); }
        function eps0(x) { return rotateRight(2, x) ^ rotateRight(13, x) ^ rotateRight(22, x); }
        function eps1(x) { return rotateRight(6, x) ^ rotateRight(11, x) ^ rotateRight(25, x); }
        function ch(x, y, z) { return (x & y) ^ (~x & z); }
        function maj(x, y, z) { return (x & y) ^ (x & z) ^ (y & z); }

        const K = [
            0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
            0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
            0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
            0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
            0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
            0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
            0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
            0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef4a394, 0x08f9898d
        ];

        let H = [0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19];

        const bytes = new TextEncoder().encode(str);
        const l = bytes.length;
        const bitLen = l * 8;

        const k = (448 - (l + 1) % 64 + 64) % 64;
        const padded = new Uint8Array(l + 1 + k + 8);
        padded.set(bytes);
        padded[l] = 0x80;

        const view = new DataView(padded.buffer);
        view.setBigUint64(padded.length - 8, BigInt(bitLen), false);

        for (let i = 0; i < padded.length; i += 64) {
            const W = new Uint32Array(64);
            for (let t = 0; t < 16; t++) {
                W[t] = view.getUint32(i + t * 4, false);
            }
            for (let t = 16; t < 64; t++) {
                W[t] = (sig1(W[t - 2]) + W[t - 7] + sig0(W[t - 15]) + W[t - 16]) >>> 0;
            }

            let [a, b, c, d, e, f, g, h] = H;

            for (let t = 0; t < 64; t++) {
                const T1 = (h + eps1(e) + ch(e, f, g) + K[t] + W[t]) >>> 0;
                const T2 = (eps0(a) + maj(a, b, c)) >>> 0;
                h = g;
                g = f;
                f = e;
                e = (d + T1) >>> 0;
                d = c;
                c = b;
                b = a;
                a = (T1 + T2) >>> 0;
            }

            H[0] = (H[0] + a) >>> 0;
            H[1] = (H[1] + b) >>> 0;
            H[2] = (H[2] + c) >>> 0;
            H[3] = (H[3] + d) >>> 0;
            H[4] = (H[4] + e) >>> 0;
            H[5] = (H[5] + f) >>> 0;
            H[6] = (H[6] + g) >>> 0;
            H[7] = (H[7] + h) >>> 0;
        }

        return H.map(h => h.toString(16).padStart(8, '0')).join('');
    }

    /**
     * Standardized Image Uploader to Server with Retry, Timeout & Validation
     * @param {Array} images - Array of image objects with file data
     * @param {string|number} productVariantId - Variant ID
     * @param {string} tokenfile - Token file
     * @param {string|number} timestamp - Timestamp
     * @param {string} [sid] - Section ID for error banner
     * @returns {Promise<Object>} Upload result object
     */
    async function uploadedImages2Server(images, productVariantId, tokenfile, timestamp, sid) {
        const endPoint = 'https://www.cruise-map.com/api/smart-map/upload-photo';
        
        if (sid) {
            const errorDiv = document.getElementById('pcw-step1-error-' + sid);
            if (errorDiv) {
                errorDiv.style.display = 'none';
                errorDiv.innerHTML = '';
            }
        }

        if (!images || !Array.isArray(images) || images.length === 0) {
            return { success: true, message: 'No images array provided', data: [], uploadedCount: 0 };
        }

        if (!productVariantId || !tokenfile) {
            throw new Error('Missing required parameters: productVariantId or token');
        }

        const imagesWithIndices = [];
        images.forEach((imgData, originalIndex) => {
            const imgFile = imgData ? (imgData.image && imgData.image.file ? imgData.image.file : (imgData.file || null)) : null;
            if (imgFile && imgFile instanceof File && imgFile.size > 0) {
                imagesWithIndices.push({
                    imageData: imgData.image || imgData,
                    originalIndex: originalIndex,
                    slotNumber: originalIndex + 1
                });
            }
        });

        if (imagesWithIndices.length === 0) {
            return { success: false, error: 'No valid images to upload', data: [], uploadedCount: 0, totalSlots: images.length };
        }

        const form = new FormData();
        form.append('product_id', String(productVariantId));
        form.append('timestamp', String(timestamp));
        form.append('token', tokenfile);
        form.append('image_count', String(images.length));

        imagesWithIndices.forEach((item) => {
            const { imageData, originalIndex } = item;
            const file = imageData.file;
            if (!file || !file.type.startsWith('image/')) return;
            const maxSize = 10 * 1024 * 1024; // 10MB
            if (file.size > maxSize) return;

            form.append(`image_file[${originalIndex}]`, file, file.name);
        });

        const uploadWithRetry = async (retries = 3) => {
            for (let attempt = 1; attempt <= retries; attempt++) {
                try {
                    const controller = new AbortController();
                    const timeoutId = setTimeout(() => controller.abort(), 45000); // 45s timeout

                    const response = await fetch(endPoint, {
                        method: 'POST',
                        body: form,
                        signal: controller.signal
                    });

                    clearTimeout(timeoutId);

                    if (!response.ok) {
                        const errorText = await response.text();
                        throw new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`);
                    }

                    const contentType = response.headers.get('content-type');
                    if (!contentType || !contentType.includes('application/json')) {
                        const responseText = await response.text();
                        throw new Error(`Expected JSON response, got: ${contentType}. Response: ${responseText}`);
                    }

                    const data = await response.json();

                    if (typeof data !== 'object' || data === null) {
                        throw new Error('Invalid response format');
                    }
                    if (data.isError) {
                        console.error('Server returned error:', data.message);
                        return {
                            success: false,
                            error: data.message || 'Server error',
                            userMessage: 'There was an error uploading your images. Please try again later.',
                            uploadedCount: 0
                        };
                    } else if (data.data && data.data.uploads < imagesWithIndices.length) {
                        console.error('Mismatch in uploaded image count:', data.data.uploads, 'expected:', imagesWithIndices.length);
                        return {
                            success: false,
                            error: 'Mismatch in uploaded image count',
                            userMessage: `Expected to upload ${imagesWithIndices.length} images, but ${imagesWithIndices.length - (data.data.uploads || 0)} image(s) may have failed. Please try again.`,
                            uploadedCount: data.data.uploads || 0
                        };
                    }

                    return {
                        success: true,
                        data: data,
                        uploadedCount: imagesWithIndices.length,
                        totalSlots: images.length,
                        uploadedPositions: imagesWithIndices.map(item => item.slotNumber),
                        message: `Successfully uploaded ${imagesWithIndices.length} image(s) from ${images.length} total slots.`
                    };

                } catch (error) {
                    console.warn(`Upload attempt ${attempt}/${retries} failed:`, error.message);
                    if (attempt === retries) {
                        return {
                            success: false,
                            error: error.message,
                            userMessage: 'Image upload timed out or failed. Please check your internet connection and try again.',
                            uploadedCount: 0
                        };
                    }
                    await new Promise(res => setTimeout(res, 1000 * attempt)); // Exponential backoff delay
                }
            }
        };

        return await uploadWithRetry(3);
    }

    /**
     * Unified Loading Spinner Controller
     * @param {string} action - 'show' or 'hide'
     * @param {number} [minDuration=0] - Minimum display duration in ms
     * @param {string} [sid] - Section ID
     * @param {boolean} [isStyleSelection=false] - Simple spinner toggle flag
     */
    let pcwSpinnerShownAt = 0;
    let pcwSpinnerTimeout = null;

    function spinner(action, minDuration = 0, sid = null, isStyleSelection = false) {
        const sectionId = sid || (typeof window.sid !== 'undefined' ? window.sid : '');
        let spinnerEl = document.getElementById('pcw-spinner-' + sectionId);

        if (sectionId) {
            const sectionSpinner = document.querySelector(`.product-customizer-wizard [id="pcw-spinner-${sectionId}"], .product-illustrated-wizard [id="pcw-spinner-${sectionId}"]`);
            if (sectionSpinner && sectionSpinner !== spinnerEl) {
                const oldSpinner = document.getElementById('pcw-spinner-' + sectionId);
                if (oldSpinner && oldSpinner.parentNode === document.body) {
                    oldSpinner.remove();
                }
                spinnerEl = sectionSpinner;
            }
        }

        if (!spinnerEl) return;

        if (pcwSpinnerTimeout) {
            clearTimeout(pcwSpinnerTimeout);
            pcwSpinnerTimeout = null;
        }

        if (action === 'show') {
            pcwSpinnerShownAt = Date.now();
            if (spinnerEl.parentNode !== document.body) {
                document.body.appendChild(spinnerEl);
            }
            if (isStyleSelection) {
                spinnerEl.classList.remove('pcw-simple-spinner');
            } else {
                spinnerEl.classList.add('pcw-simple-spinner');
            }
            spinnerEl.style.display = 'flex';
        } else {
            const elapsed = Date.now() - pcwSpinnerShownAt;
            const delay = Math.max(0, minDuration - elapsed);

            pcwSpinnerTimeout = setTimeout(() => {
                spinnerEl.style.display = 'none';
                pcwSpinnerTimeout = null;
                spinnerEl.classList.remove('pcw-simple-spinner');
            }, delay);
        }
    }

    /**
     * LocalStorage Helper with Expiration and Safari Private Mode Safety
     */
    function loadDataFromCache(keyData, keyTimestamp, lifespanMs = 24 * 60 * 60 * 1000) {
        try {
            const data = localStorage.getItem(keyData);
            const timestamp = localStorage.getItem(keyTimestamp);
            if (data && timestamp) {
                const now = Date.now();
                if (now - parseInt(timestamp, 10) < lifespanMs) {
                    return JSON.parse(data);
                }
            }
        } catch (e) {
            console.warn('LocalStorage load error:', e);
        }
        return null;
    }

    /**
     * Date Range Formatter Helper
     */
    function formatDateRange(startDateStr, endDateStr, locale = 'en') {
        if (!startDateStr || !endDateStr) return '';
        try {
            const months = {
                en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
                de: ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember']
            };
            const currentLang = (locale || 'en').toLowerCase().startsWith('de') ? 'de' : 'en';

            let mStart = typeof moment !== 'undefined' ? moment(startDateStr) : null;
            let mEnd = typeof moment !== 'undefined' ? moment(endDateStr) : null;

            let startYear, startMonthIdx, startDay;
            let endYear, endMonthIdx, endDay;

            if (mStart && mStart.isValid() && mEnd && mEnd.isValid()) {
                startYear = mStart.year();
                startMonthIdx = mStart.month();
                startDay = mStart.date();
                endYear = mEnd.year();
                endMonthIdx = mEnd.month();
                endDay = mEnd.date();
            } else {
                const startParts = startDateStr.split('-').map(Number);
                const endParts = endDateStr.split('-').map(Number);
                if (startParts.length !== 3 || endParts.length !== 3) return `${startDateStr} - ${endDateStr}`;
                startYear = startParts[0];
                startMonthIdx = startParts[1] - 1;
                startDay = startParts[2];
                endYear = endParts[0];
                endMonthIdx = endParts[1] - 1;
                endDay = endParts[2];
            }

            const startMonth = months[currentLang][startMonthIdx] || '';
            const endMonth = months[currentLang][endMonthIdx] || '';

            const pad = (n) => String(n).padStart(2, '0');
            const sDay = pad(startDay);
            const eDay = pad(endDay);

            if (startYear === endYear) {
                if (startMonthIdx === endMonthIdx) {
                    if (startDay === endDay) {
                        return currentLang === 'de'
                            ? `${sDay}. ${startMonth} ${startYear}`
                            : `${startMonth} ${sDay}, ${startYear}`;
                    }
                    return currentLang === 'de'
                        ? `${sDay}. bis ${eDay}. ${startMonth} ${startYear}`
                        : `${startMonth} ${sDay}–${eDay}, ${startYear}`;
                }
                return currentLang === 'de'
                    ? `${sDay}. ${startMonth} bis ${eDay}. ${endMonth} ${startYear}`
                    : `${startMonth} ${sDay} – ${endMonth} ${eDay}, ${startYear}`;
            }

            return currentLang === 'de'
                ? `${sDay}. ${startMonth} ${startYear} bis ${eDay}. ${endMonth} ${endYear}`
                : `${startMonth} ${sDay}, ${startYear} – ${endMonth} ${eDay}, ${endYear}`;

        } catch (e) {
            console.error('Error formatting date range:', e);
            return `${startDateStr} - ${endDateStr}`;
        }
    }

    /**
     * Modal Backdrop & Popup Helpers
     */
    function createBackdrop() {
        const backdrop = document.createElement('div');
        backdrop.style.position = 'fixed';
        backdrop.style.top = '0';
        backdrop.style.left = '0';
        backdrop.style.width = '100vw';
        backdrop.style.height = '100vh';
        backdrop.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
        backdrop.style.backdropFilter = 'blur(4px)';
        backdrop.style.zIndex = '999998';
        backdrop.style.display = 'flex';
        backdrop.style.justifyContent = 'center';
        backdrop.style.alignItems = 'center';
        return backdrop;
    }

    function createPopup() {
        const popup = document.createElement('div');
        popup.style.position = 'relative';
        popup.style.backgroundColor = '#fff';
        popup.style.borderRadius = '8px';
        popup.style.padding = '20px';
        popup.style.maxWidth = '90vw';
        popup.style.maxHeight = '90vh';
        popup.style.overflow = 'auto';
        popup.style.boxShadow = '0 10px 25px rgba(0,0,0,0.5)';
        popup.style.zIndex = '999999';
        return popup;
    }

    /**
     * Delivery Type Key Lookup Helper
     */
    function getDeliveryTypeKey(variantId, deliveryVariants) {
        if (!deliveryVariants || !variantId) return '';
        const variant = deliveryVariants.find(item => item.id == variantId);
        if (!variant) return '';
        
        const title = (variant.title || '').toLowerCase();
        const opt2 = (variant.option2 || '').toLowerCase();
        
        if (title.includes('wall art') || opt2.includes('wall art')) {
            return 'wall_art';
        }

        if (title.includes('puzzle') || opt2.includes('puzzle')) {
            return 'puzzle';
        }
        
        if (title.includes('blogger') || opt2.includes('blogger')) {
            return 'free_blogger';
        }
        
        const isSocialMyCruise = title.includes('social my cruise') || 
                                 title.includes('social "my cruise"') || 
                                 (title.includes('social') && title.includes('my cruise')) ||
                                 opt2.includes('social my cruise') ||
                                 opt2.includes('social "my cruise"') ||
                                 (opt2.includes('social') && opt2.includes('my cruise')) ||
                                 title.includes('social personalized') ||
                                 opt2.includes('social personalized'); 
        if (title.includes('social pure') || opt2.includes('social pure')) {
            return 'free_social';
        }
        
        if (title.includes('mobile') || opt2.includes('mobile')) {
            return 'free_mobile';
        }
        if (isSocialMyCruise) {
            return 'my_social';
        }
        
        if (title.includes('jpg') || opt2.includes('jpg')) {
            return 'jpg';
        }
        
        if (title.includes('pdf') || opt2.includes('pdf')) {
            return 'pdf';
        }
        
        return '';
    }

    // Expose Module on window scope
    const PCWUtils = {
        PCWTooltip,
        sha256,
        uploadedImages2Server,
        spinner,
        loadDataFromCache,
        formatDateRange,
        createPopup,
        createBackdrop,
        getDeliveryTypeKey
    };
    const moneyFormat = {
      format: function(amount) {
        const val = typeof amount === 'number' ? amount : (parseFloat(amount) || 0);
        const iso = (typeof window !== 'undefined' && window.ISO_CODE) || (typeof ISO_CODE !== 'undefined' && ISO_CODE) || '';
        if (iso.toLowerCase() === 'us') {
          const num = new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }).format(val);
          return '$' + num;
        }
        const num = new Intl.NumberFormat('de-DE', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(val);
        return num + ' €';
      }
    };
    window.moneyFormat = moneyFormat;
    window.PCWUtils = PCWUtils;

    // Direct Window Function Aliases for 100% Backwards Compatibility
    window.PCWTooltip = PCWTooltip;
    window.sha256 = sha256;
    window.uploadedImages2Server = uploadedImages2Server;
    window.spinner = spinner;
    window.loadDataFromCache = loadDataFromCache;
    window.formatDateRange = formatDateRange;
    window.createPopup = createPopup;
    window.createBackdrop = createBackdrop;
    window.getDeliveryTypeKey = getDeliveryTypeKey;

})(window, document);
